/* eslint-disable max-lines-per-function */
import { HttpErrorResponse } from '@angular/common/http';

import { createSelector } from '@ngrx/store';

import type { Position } from '@recruitee/http-types';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  ID,
  memoizeSelector,
  pipeState,
} from '@recruitee/ngrx';
import type { TalentPool, TalentPoolData } from '@recruitee/talent-pools-types';

import { TalentPoolsScope } from './talent-pool.types';

const { actions, selectors, reducer, namespace } = createEntityCollectionStore<TalentPool>()(
  'TalentPoolsNew',
  {
    customActions: adapter => {
      const actions = getEntityCollectionActions(adapter, ({ id }) => id!);
      return {
        fetchOne: (state, payload: { id: number | string }) =>
          adapter.markAsPending(state, { scope: null, entities: [payload.id] }),
        fetchOneSuccess: (state, payload: { id: number | string; talentPool: TalentPool }) =>
          pipeState(
            state,
            next => adapter.replaceOne(next, payload.id, payload.talentPool),
            next => adapter.markAsLoaded(next, { scope: null, entities: [payload.id] }),
          ),
        fetchOneFailed: (state, payload: { id: number | string; error: HttpErrorResponse }) =>
          adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
        fetchCollection: (state, payload: { scope: TalentPoolsScope }) =>
          adapter.markAsPending(state),
        duplicate: (
          state,
          payload: {
            data: { id: number };
          },
        ) => adapter.markAsPending(state),
        duplicateSuccess: (state, payload: { data: TalentPool }) =>
          pipeState(
            state,
            next =>
              adapter.addOne(next, payload.data, {
                scope: TalentPoolsScope.Default,
                prepend: true,
              }),
            next => adapter.markAsLoaded(next),
          ),
        duplicateFailed: actions.createOnePessimisticFailed,
        archive: (state, payload: { id: ID }) => adapter.markAsPending(state),
        archiveSuccess: (state, payload: { data: TalentPool }) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.data.id),
            next =>
              adapter.addOne(next, payload.data, {
                scope: TalentPoolsScope.Archived,
              }),
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: '' }),
          ),
        archiveFailed: actions.updateOnePessimisticFailed,
        retrieve: (state, payload: { id: ID }) => adapter.markAsPending(state),
        retrieveSuccess: (state, payload: { data: TalentPool }) =>
          pipeState(
            state,
            next => adapter.removeOne(next, payload.data.id),
            next =>
              adapter.addOne(next, payload.data, {
                scope: TalentPoolsScope.Default,
              }),
            next => adapter.markAsLoaded(next, { entities: [payload.data.id], scope: '' }),
          ),
        retrieveFailed: actions.updateOnePessimisticFailed,
        follow: (state, payload: { id: ID }) => adapter.markAsPending(state),
        followSuccess: actions.updateOnePessimisticSuccess,
        followFailed: actions.updateOnePessimisticFailed,
        unfollow: (state, payload: { id: ID }) => adapter.markAsPending(state),
        unfollowSuccess: actions.updateOnePessimisticSuccess,
        unfollowFailed: actions.updateOnePessimisticFailed,
        updatePositions: (state, payload: { positions: Position[] }) =>
          pipeState(state, next =>
            adapter.markAsPending(next, { scope: TalentPoolsScope.Default, entities: [] }),
          ),
        updatePositionSuccess: (state, payload: Position[]) => {
          const updated = [...payload]
            .sort((a, b) => b.position - a.position)
            .map(({ id, position }) => ({
              ...state.entityMap[id]!,
              position: position,
            }));
          return pipeState(
            state,
            next => adapter.setMany(next, updated, { scope: TalentPoolsScope.Default }),
            next => adapter.markAsLoaded(next, { scope: TalentPoolsScope.Default, entities: [] }),
          );
        },
        updatePositionsFailed: (state, payload: { error: HttpErrorResponse }) =>
          pipeState(state, next =>
            adapter.setError(next, payload.error, {
              scope: TalentPoolsScope.Default,
              entities: [],
            }),
          ),
        updateOnePessimistic: (state, payload: { data: TalentPoolData & { id: number } }) =>
          adapter.markAsPending(state),
        updateOnePessimisticSuccess: (state, payload: { data: TalentPool }) =>
          actions.updateOnePessimisticSuccess(state, payload),
        updateOnePessimisticFailed: (
          state,
          payload: { id: number | string; error: HttpErrorResponse },
        ) => adapter.setError(state, payload.error, { scope: null, entities: [payload.id] }),
        createOnePessimistic: (state, payload: { data: TalentPoolData }) =>
          adapter.markAsPending(state),
      };
    },
  },
);

const byIdOrSlug = memoizeSelector((identifier: number | string) =>
  createSelector(selectors.allEntities, (state: TalentPool[]) =>
    state.find(offer => offer.slug === identifier || offer.id === identifier),
  ),
);

const metaByIdOrSlug = memoizeSelector((identifier: number | string) => selectors.meta(identifier));

const TalentPoolsNewSelectors = {
  ...selectors,
  entities: memoizeSelector((archived: boolean = false) =>
    selectors.entities({ scope: archived ? TalentPoolsScope.Archived : TalentPoolsScope.Default }),
  ),
  byIdOrSlug,
  metaByIdOrSlug,
};

export {
  TalentPoolsNewSelectors,
  actions as TalentPoolsNewActions,
  reducer as TalentPoolsReducer,
  namespace as TalentPoolsNamespace,
};
